import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { BUSINESS, NAV, SERVICE_AREAS } from "../data/site";
import { useRequestModal } from "./RequestModal";

export default function Footer() {
  const { openRequest } = useRequestModal();
  return (
    <footer className="bg-ink text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src={BUSINESS.logoDark} alt="Yavapai Waste Solutions" className="hidden md:block h-16 w-auto mb-4" />
            <p className="text-sm leading-relaxed text-slate-400">
              Locally owned, veteran-owned dumpster rentals and waste service across Yavapai County. We talk trash — so you don't have to worry about it.
            </p>
            <div className="flex gap-3 mt-5">
              <a href={BUSINESS.facebook} target="_blank" rel="noreferrer" aria-label="YWS on Facebook" data-testid="footer-facebook" className="p-2 rounded-lg bg-white/5 hover:bg-sky-600 hover:text-white transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={BUSINESS.instagram} target="_blank" rel="noreferrer" aria-label="YWS on Instagram" data-testid="footer-instagram" className="p-2 rounded-lg bg-white/5 hover:bg-sky-600 hover:text-white transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wider mb-4">Services</h3>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/roll-off-dumpsters" className="hover:text-sky-400 transition-colors" data-testid="footer-roll-off">Roll-Off Dumpster Rentals</Link></li>
              <li><Link to="/dumpster-size-guide" className="hover:text-sky-400 transition-colors" data-testid="footer-size-guide">Dumpster Size Guide</Link></li>
              <li><Link to="/contractor-dumpster-rentals" className="hover:text-sky-400 transition-colors" data-testid="footer-contractors">Contractor Dumpster Service</Link></li>
              <li><Link to="/front-load/residential" className="hover:text-sky-400 transition-colors" data-testid="footer-residential">Residential Front-Load</Link></li>
              <li><Link to="/front-load/commercial" className="hover:text-sky-400 transition-colors" data-testid="footer-commercial">Commercial Front-Load</Link></li>
              <li><Link to="/gallery" className="hover:text-sky-400 transition-colors" data-testid="footer-gallery">Gallery / Our Work</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wider mb-4">Service Areas</h3>
            <ul className="grid grid-cols-2 gap-2.5 text-sm">
              {SERVICE_AREAS.map((a) => (
                <li key={a.slug}>
                  <Link to={`/service-areas/${a.slug}`} className="hover:text-sky-400 transition-colors" data-testid={`footer-area-${a.slug}`}>
                    {a.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wider mb-4">Contact</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <a href={BUSINESS.phoneHref} className="hover:text-sky-400 transition-colors" data-testid="footer-phone">{BUSINESS.phone}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <a href={`mailto:${BUSINESS.email}`} className="hover:text-sky-400 transition-colors" data-testid="footer-email">{BUSINESS.email}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <span>{BUSINESS.address}</span>
              </li>
            </ul>
            <p className="text-xs text-slate-500 mt-3">{BUSINESS.hours}</p>
            <button
              onClick={() => openRequest()}
              className="mt-5 inline-flex px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-heading font-semibold transition-colors"
              data-testid="footer-request-dumpster"
            >
              Request a Dumpster
            </button>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} Yavapai Waste Solutions LLC. All rights reserved.</span>
          <nav className="flex gap-5" aria-label="Footer">
            <Link to="/about" className="hover:text-sky-400 transition-colors">About</Link>
            <Link to="/resources" className="hover:text-sky-400 transition-colors">Resources</Link>
            <Link to="/contact" className="hover:text-sky-400 transition-colors">Contact</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
